import { useState, useEffect, useCallback } from 'react';
import { getSkillQuestion } from '../lib/api';

/**
 * useSkillQuestion
 *
 * Fetches a random skill question for raffle entry via Cloud Function.
 *
 * Security Notes:
 * - Uses Cloud Function to fetch question (NOT direct Firestore)
 * - The correctAnswer is NEVER sent to the client
 * - Answer validation happens SERVER-SIDE in purchaseEntry Cloud Function
 * - This prevents cheating by inspecting network requests or Firestore
 */
export function useSkillQuestion() {
  const [question, setQuestion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchQuestion = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await getSkillQuestion();

      if (!result.success || !result.question) {
        throw new Error('Failed to load question');
      }

      // Question from Cloud Function already excludes correctAnswer
      setQuestion(result.question);
    } catch (err) {
      console.error('Error fetching skill question:', err);
      setError(err.message || 'Failed to load question');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch on mount
  useEffect(() => {
    fetchQuestion();
  }, [fetchQuestion]);

  // Function to get a new question (for retry after wrong answer)
  const refreshQuestion = useCallback(() => {
    fetchQuestion();
  }, [fetchQuestion]);

  return {
    question,
    loading,
    error,
    refreshQuestion,
  };
}

/**
 * Skill question data structure returned from Cloud Function:
 *
 * {
 *   id: "questionId",
 *   question: "What is the capital of Ireland?",
 *   options: ["Dublin", "Cork", "Galway"],
 *   // correctAnswer is NOT included - server-side only
 * }
 */
